'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { tryCatch } from '@shared/utils';
import { Logger } from '@shared/logger';
import { TIME_CONVERSION } from '@shared/constants';
import { SCANNER_CAPTURE_CONSTANTS } from '@/src/constants/SCANNER_CAPTURE_CONSTANTS';

/**
 * @group Hooks
 * @summary
 * <blockquote>Hook for capturing video frames, converting to BGR24, and sending via WebSocket</blockquote>
 * 
 * @example
 * ```tsx
 * const { startCapture, stopCapture, framesSent } = useFrameCapture({
 *   stream,
 *   sendFrame: (buffer) => webSocketHook.sendFrame(buffer),
 *   targetFps: 15,
 *   videoElement,
 *   canvasElement,
 * });
 * 
 * startCapture();
 * ```
 */
interface UseFrameCaptureOptions {
  stream: MediaStream | null;
  sendFrame: ((buffer: ArrayBuffer) => void) | null;
  targetFps: number;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
}

interface UseFrameCaptureReturn {
  startCapture: () => void;
  stopCapture: () => void;
  framesSent: number;
}

/**
 * Round value to nearest even number (minimum 2)
 */
// Minimum even value for dimensions (2 pixels)
const MIN_EVEN_DIMENSION = 2;
const VIDEO_READY_STATE_HAVE_CURRENT_DATA = 2;

function roundToEven(value: number): number {
  const rounded = Math.max(MIN_EVEN_DIMENSION, Math.round(value));
  return rounded % 2 === 0 ? rounded : rounded - 1;
}

/**
 * Get capture region based on video dimensions and display size
 */
function getCaptureRegion(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement
): {
  sx: number;
  sy: number;
  sWidth: number;
  sHeight: number;
  targetWidth: number;
  targetHeight: number;
} | null {
  const sourceWidth = video.videoWidth;
  const sourceHeight = video.videoHeight;
  const displayWidth = video.clientWidth || sourceWidth;
  const displayHeight = video.clientHeight || sourceHeight;

  if (!sourceWidth || !sourceHeight || !displayWidth || !displayHeight) {
    return null;
  }

  const sourceAspect = sourceWidth / sourceHeight;
  const displayAspect = displayWidth / displayHeight;

  let sx = 0;
  let sy = 0;
  let sWidth = sourceWidth;
  let sHeight = sourceHeight;

  // Crop source to match display aspect ratio
  if (sourceAspect > displayAspect) {
    sWidth = roundToEven(sourceHeight * displayAspect);
    sx = Math.max(0, Math.floor((sourceWidth - sWidth) / 2));
  } else if (sourceAspect < displayAspect) {
    sHeight = roundToEven(sourceWidth / displayAspect);
    sy = Math.max(0, Math.floor((sourceHeight - sHeight) / 2));
  }

  const cropAspect = sWidth / sHeight;
  let targetWidth: number;
  let targetHeight: number;

  // Scale to max long side
  if (cropAspect >= 1) {
    targetWidth = SCANNER_CAPTURE_CONSTANTS.CAPTURE_MAX_LONG_SIDE;
    targetHeight = SCANNER_CAPTURE_CONSTANTS.CAPTURE_MAX_LONG_SIDE / cropAspect;
  } else {
    targetHeight = SCANNER_CAPTURE_CONSTANTS.CAPTURE_MAX_LONG_SIDE;
    targetWidth = SCANNER_CAPTURE_CONSTANTS.CAPTURE_MAX_LONG_SIDE * cropAspect;
  }

  return {
    sx,
    sy,
    sWidth,
    sHeight,
    targetWidth: roundToEven(targetWidth),
    targetHeight: roundToEven(targetHeight),
  };
}

/**
 * Extract BGR24 bytes from canvas ImageData
 */
function extractBGR24(imageData: ImageData): Uint8Array {
  const { data: rgba, width, height } = imageData;
  const bgr24 = new Uint8Array(width * height * 3);

  for (let i = 0; i < rgba.length; i += 4) {
    const pixelIndex = (i / 4) * 3;
    bgr24[pixelIndex] = rgba[i + 2]; // B
    bgr24[pixelIndex + 1] = rgba[i + 1]; // G
    bgr24[pixelIndex + 2] = rgba[i]; // R
  }

  return bgr24;
}

/**
 * Build frame message: [4B width BE][4B height BE][BGR24 bytes]
 */
function buildFrameMessage(
  width: number,
  height: number,
  bgr24Bytes: Uint8Array
): ArrayBuffer {
  const total = 8 + bgr24Bytes.length;
  const buffer = new ArrayBuffer(total);
  const view = new DataView(buffer);
  view.setUint32(0, width, false); // Big-endian
  view.setUint32(4, height, false); // Big-endian
  new Uint8Array(buffer).set(bgr24Bytes, 8);
  return buffer;
}

export function useFrameCapture(
  options: UseFrameCaptureOptions
): UseFrameCaptureReturn {
  const { stream, sendFrame, targetFps, videoRef, canvasRef } = options;
  const [framesSent, setFramesSent] = useState(0);
  const captureIntervalRef = useRef<number | null>(null);
  const lastCaptureTimeRef = useRef<number>(0);

  const captureAndSend = useCallback(async (): Promise<void> => {
    const videoElement = videoRef.current;
    const canvasElement = canvasRef.current;

    if (!sendFrame || !videoElement || !canvasElement || !stream) {
      return;
    }

    // Check if video is ready
    if (videoElement.readyState < VIDEO_READY_STATE_HAVE_CURRENT_DATA) {
      return;
    }

    // Check WebSocket backpressure (if available via sendFrame context)
    // Note: We can't directly check ws.bufferedAmount here, but the sendFrame
    // callback should handle backpressure checking if needed

    const region = getCaptureRegion(videoElement, canvasElement);
    if (!region) {
      return;
    }

    canvasElement.width = region.targetWidth;
    canvasElement.height = region.targetHeight;

    const ctx = canvasElement.getContext('2d');
    if (!ctx) {
      return;
    }

    const captureResult = tryCatch(() => {
      ctx.drawImage(
        videoElement,
        region.sx,
        region.sy,
        region.sWidth,
        region.sHeight,
        0,
        0,
        region.targetWidth,
        region.targetHeight
      );

      const imageData = ctx.getImageData(0, 0, region.targetWidth, region.targetHeight);
      const bgr24Bytes = extractBGR24(imageData);

      return buildFrameMessage(
        region.targetWidth,
        region.targetHeight,
        bgr24Bytes
      );
    });

    if (captureResult.isErr) {
      Logger.error('Frame capture error', {
        resource: 'scanner.frame_capture',
      }, captureResult.error instanceof Error ? captureResult.error : new Error(String(captureResult.error)));
      return;
    }

    sendFrame(captureResult.value);
    setFramesSent((prev) => prev + 1);
  }, [sendFrame, videoRef, canvasRef, stream]);

  const scheduleNextCapture = useCallback(() => {
    const videoElement = videoRef.current;
    const canvasElement = canvasRef.current;

    if (!sendFrame || !videoElement || !canvasElement || !stream) {
      return;
    }

    const now = performance.now();
    const elapsed = now - lastCaptureTimeRef.current;
    const intervalMs = TIME_CONVERSION.MILLISECONDS_PER_SECOND / targetFps;
    const delay = Math.max(0, intervalMs - elapsed);

    captureIntervalRef.current = window.setTimeout(() => {
      lastCaptureTimeRef.current = performance.now();
      captureAndSend().finally(() => {
        scheduleNextCapture();
      });
    }, delay);
  }, [sendFrame, videoRef, canvasRef, stream, targetFps, captureAndSend]);

  const startCapture = useCallback(() => {
    if (captureIntervalRef.current !== null) {
      return; // Already capturing
    }

    const videoElement = videoRef.current;
    const canvasElement = canvasRef.current;

    if (!sendFrame || !videoElement || !canvasElement || !stream) {
      Logger.warn('Cannot start capture: missing dependencies', {
        resource: 'scanner.frame_capture',
      });
      return;
    }

    lastCaptureTimeRef.current = performance.now();
    scheduleNextCapture();
  }, [sendFrame, videoRef, canvasRef, stream, scheduleNextCapture]);

  const stopCapture = useCallback(() => {
    if (captureIntervalRef.current !== null) {
      clearTimeout(captureIntervalRef.current);
      captureIntervalRef.current = null;
    }
  }, []);

  // Cleanup on unmount or when dependencies change
  useEffect(() => {
    return () => {
      stopCapture();
    };
  }, [stopCapture]);

  // Stop capture if dependencies become unavailable
  useEffect(() => {
    const videoElement = videoRef.current;
    const canvasElement = canvasRef.current;

    if (!sendFrame || !videoElement || !canvasElement || !stream) {
      stopCapture();
    }
  }, [sendFrame, videoRef, canvasRef, stream, stopCapture]);

  return {
    startCapture,
    stopCapture,
    framesSent,
  };
}
